// One coordinator for the existing refresh endpoint and localStorage keys.
export const REFRESH_TIMEOUT_MS = 15000;
export const REFRESH_MAX_ATTEMPTS = 3;
const BACKOFF_MS = [1000, 2000];
const EXHAUSTION_COOLDOWN_MS = 30000;
export const RETRY_AFTER_CAP_MS = 60000;

export function classifyRefreshError(error) {
  const status = error.response?.status;
  if (status === 401 || status === 403) return 'rejected';
  if (status === 400 || status === 422) return 'contract';
  if (status === 429 || (status >= 500 && status <= 599)) return 'transient';
  if (!error.response && ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code)) return 'transient';
  return 'contract';
}

export function retryAfterMs(value, now = Date.now()) {
  if (value == null || String(value).trim() === '') return null;
  const text = String(value).trim();
  if (/^[+-]/.test(text)) return null;
  if (/^\d+(\.\d+)?$/.test(text)) {
    const ms = Number(text) * 1000;
    return Number.isFinite(ms) ? ms : null;
  }
  const date = Date.parse(text);
  return Number.isFinite(date) ? Math.max(0, date - now) : null;
}

export function createRefreshCoordinator({ request, storage, generation, onUnresolved, onRejected, onSuccess, onStorageFailure = () => {}, validateAccess = () => true }) {
  let active = null;
  let held = null;
  let fatalStorageFailure = null;
  const read = () => ({ access: storage.getItem('access_token'), refresh: storage.getItem('refresh_token') });
  const same = (a, b) => a.access === b.access && a.refresh === b.refresh;
  const valid = token => typeof token === 'string' && token.trim().length > 0;
  const abandoned = () => ({ success: false, kind: 'abandoned', reason: 'abandoned' });

  function invalidate() {
    const previous = active;
    active = null;
    held = null;
    previous?.controller.abort();
  }

  function wait(ms, signal, cycle) {
    return new Promise(resolve => {
      if (signal.aborted) return resolve(false);
      const finish = value => { clearTimeout(timer); signal.removeEventListener('abort', cancel); if (cycle) cycle.skipWait = null; resolve(value); };
      const cancel = () => finish(false);
      const timer = setTimeout(() => finish(true), ms);
      signal.addEventListener('abort', cancel, { once: true });
      if (cycle) cycle.skipWait = () => { if (Date.now() >= cycle.rateLimitUntil) finish(true); };
    });
  }

  function refresh(reason = 'unknown', { userInitiated = false } = {}) {
    if (fatalStorageFailure) return Promise.resolve(fatalStorageFailure);
    const started = generation();
    let previous;
    try { previous = read(); } catch {
      // Wait for applicable work rather than report completion while it runs.
      // Its existing current() checks must still verify the credential snapshot
      // before any response or rotated credentials can be applied.
      if (active && active.started === started && !active.controller.signal.aborted) return active.promise;
      onUnresolved();
      return Promise.resolve({ success: false, kind: 'storage', reason: 'credential_read_failed' });
    }
    if (active && active.started === started && same(active.previous, previous)) {
      if (userInitiated) active.skipWait?.();
      return active.promise;
    }
    if (active) invalidate();
    if (held && held.started === started && same(held.previous, previous) && Date.now() < held.until) {
      if (userInitiated && !(held.result.rateLimitUntil > Date.now())) held = null;
      else { onUnresolved(held.result); return Promise.resolve(held.result); }
    }
    if (!valid(previous.refresh)) {
      onUnresolved();
      return Promise.resolve({ success: false, kind: 'contract', reason: 'no_refresh_token' });
    }
    onUnresolved();
    const cycle = { started, previous, controller: new AbortController(), promise: null, rateLimitUntil: 0, skipWait: null };
    const current = () => {
      try { return active === cycle && generation() === started && !cycle.controller.signal.aborted && same(read(), previous); }
      catch { return false; }
    };
    active = cycle;
    const stop = (result, delay = Infinity) => {
      if (active === cycle && generation() === started) {
        let snapshot = previous;
        try { snapshot = read(); } catch { /* Preserve the failure result. */ }
        const until = delay === Infinity ? Infinity : Date.now() + delay;
        result.retryAt = Number.isFinite(until) ? until : null;
        held = { started, previous: snapshot, result, until };
      }
      onUnresolved(result);
      return result;
    };
    // Defer execution so all synchronous triggers can join the same promise.
    cycle.promise = Promise.resolve().then(async () => {
      for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt += 1) {
        if (!current()) return abandoned();
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return { success: false, kind: 'transient', reason: 'offline' };
        let response;
        try {
          onUnresolved({ pending: true, phase: 'request', attempt: attempt + 1 });
          response = await request(previous.refresh, { timeout: REFRESH_TIMEOUT_MS, signal: cycle.controller.signal, reason });
        } catch (error) {
          if (!current()) return abandoned();
          const kind = classifyRefreshError(error);
          const result = { success: false, kind, reason: kind === 'rejected' ? 'refresh_token_rejected' : kind === 'contract' ? 'refresh_contract_failed' : 'refresh_transient_failed', status: error.response?.status };
          if (kind === 'rejected') { onRejected(result); return result; }
          if (kind !== 'transient') return stop(result);
          const header = error.response?.headers?.['retry-after'] ?? error.response?.headers?.get?.('retry-after');
          const parsed = result.status === 429 ? retryAfterMs(header) : null;
          const retryAfter = parsed === null ? null : Math.min(parsed, RETRY_AFTER_CAP_MS);
          cycle.rateLimitUntil = retryAfter ? Date.now() + retryAfter : 0;
          result.rateLimitUntil = cycle.rateLimitUntil;
          const delay = Math.max(BACKOFF_MS[attempt] || EXHAUSTION_COOLDOWN_MS, retryAfter || 0);
          if (attempt === REFRESH_MAX_ATTEMPTS - 1) return stop(result, Math.max(EXHAUSTION_COOLDOWN_MS, delay));
          onUnresolved({ ...result, pending: true, phase: 'waiting', attempt: attempt + 1, retryAt: Date.now() + delay });
          if (!await wait(delay, cycle.controller.signal, cycle)) return abandoned();
          continue;
        }
        if (!current()) return abandoned();
        const access = response?.data?.access_token;
        const rotated = response?.data?.refresh_token;
        let validPair = valid(access) && valid(rotated);
        try { validPair = validPair && validateAccess(access); } catch { validPair = false; }
        if (!validPair) return stop({ success: false, kind: 'contract', reason: 'malformed_refresh_response' });
        try {
          storage.setItem('access_token', access);
          if (storage.getItem('access_token') !== access) throw new Error('access_write_failed');
          if (generation() !== started || active !== cycle) return abandoned();
          storage.setItem('refresh_token', rotated);
          if (storage.getItem('refresh_token') !== rotated || storage.getItem('access_token') !== access) throw new Error('pair_write_failed');
        } catch {
          // Separate writes are not atomic. Only restore our own partial pair.
          if (generation() !== started || active !== cycle) return abandoned();
          let rollbackVerified = false;
          try {
            const found = read();
            if (same(found, previous)) rollbackVerified = true;
            else if ((found.access === access || found.access === previous.access) && (found.refresh === rotated || found.refresh === previous.refresh)) {
              if (previous.access === null) storage.removeItem('access_token'); else storage.setItem('access_token', previous.access);
              if (generation() !== started || active !== cycle) return abandoned();
              storage.setItem('refresh_token', previous.refresh);
              rollbackVerified = same(read(), previous);
            }
          } catch { /* Storage remains unusable; never authenticate this pair. */ }
          if (generation() !== started || active !== cycle) return abandoned();
          if (!rollbackVerified) {
            // Attempt both removals independently, then verify rather than assume.
            for (const key of ['access_token', 'refresh_token']) {
              if (generation() !== started || active !== cycle) return abandoned();
              try { storage.removeItem(key); } catch { /* Continue attempting the other key. */ }
            }
            if (generation() !== started || active !== cycle) return abandoned();
            let credentialsCleared = false;
            try { credentialsCleared = same(read(), { access: null, refresh: null }); } catch { /* Unknown means not verified. */ }
            fatalStorageFailure = { success: false, kind: 'storage', reason: 'credential_storage_unrecoverable', rollbackVerified: false, credentialsCleared };
            onStorageFailure(fatalStorageFailure);
            return fatalStorageFailure;
          }
          return stop({ success: false, kind: 'storage', reason: 'credential_write_failed', rollbackVerified });
        }
        if (generation() !== started || active !== cycle || cycle.controller.signal.aborted) return abandoned();
        held = null;
        const result = { success: true, kind: 'success', accessToken: access, refreshToken: rotated };
        onSuccess(result);
        return result;
      }
      return abandoned();
    }).finally(() => { if (active === cycle) active = null; });
    return cycle.promise;
  }
  const recoveryState = () => ({ fatal: Boolean(fatalStorageFailure), failure: fatalStorageFailure, pending: Boolean(active), retryAt: held?.result.kind === 'transient' ? held.until : null });
  // Only a new, backend-acknowledged login with verified writes clears quarantine.
  const resetAfterLogin = () => { invalidate(); fatalStorageFailure = null; };
  return { refresh, invalidate, recoveryState, resetAfterLogin };
}
